import React, { useState, useEffect } from 'react';
import { Plus, X, Pencil, Trash2 } from 'lucide-react';
import { updateDecorativeImage, addDecorativeImage, deleteDecorativeImage, uploadDecorativeImage } from '../../firebase/decorativeImages';
import { DeleteConfirmationModal } from './Modals';

export const DecorativeImageEditor = ({ isOpen, onClose, ownerId, imageToEdit, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [linkUrl, setLinkUrl] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [imageUploadMethod, setImageUploadMethod] = useState('file');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            if (imageToEdit) {
                const initialImageUrl = imageToEdit.imageUrl ?? '';
                setName(imageToEdit.name ?? '');
                setDescription(imageToEdit.description ?? '');
                setPrice(imageToEdit.price != null ? String(imageToEdit.price) : '');
                setCurrency(imageToEdit.currency ?? 'USD');
                setLinkUrl(imageToEdit.linkUrl || '');
                setImageUrl(initialImageUrl);
                if (initialImageUrl && !initialImageUrl.startsWith('data:') && !initialImageUrl.startsWith('blob:')) {
                    setImageUploadMethod('url');
                } else {
                    setImageUploadMethod('file');
                }
            } else {
                setName('');
                setDescription('');
                setPrice('');
                setCurrency('USD');
                setLinkUrl('');
                setImageUrl('');
                setImageUploadMethod('file');
            }
            setUploadProgress(0);
            setError('');
            setIsSubmitting(false);
        }
    }, [imageToEdit, isOpen]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const url = await uploadDecorativeImage(file, (progress) => setUploadProgress(progress));
                setImageUrl(url);
                setUploadProgress(100);
            } catch (err) {
                setError('Image upload failed.');
                console.error(err);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        
        const imageData = {
            name,
            description,
            price: parseFloat(price) || 0,
            currency,
            linkUrl: linkUrl || null,
            imageUrl: imageUrl || null,
        };

        try {
            await onSave(imageData);
            onClose();
        } catch (err) {
            setError('Failed to save image.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[80] p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md relative max-h-[90vh] overflow-y-auto">
                <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <X className="h-5 w-5" />
                </button>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">{imageToEdit ? 'Edit Image' : 'Add New Image'}</h2>
                    
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Image Name</label>
                        <input
                            type="text"
                            value={name || ''}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>
                    
                    <div>
                        <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Image</label>
                         <div className="flex items-center space-x-4 my-2">
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" name="imageUploadMethod" value="file" checked={imageUploadMethod === 'file'} onChange={() => setImageUploadMethod('file')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Upload File</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" name="imageUploadMethod" value="url" checked={imageUploadMethod === 'url'} onChange={() => setImageUploadMethod('url')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">From URL</span>
                            </label>
                        </div>
                        {imageUploadMethod === 'file' ? (
                            <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full mt-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:text-gray-400 dark:file:bg-gray-700 dark:file:text-gray-300 dark:hover:file:bg-gray-600 cursor-pointer" />
                        ) : (
                             <input type="url" value={imageUrl || ''} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://example.com/image.png" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                        )}
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2 dark:bg-gray-700">
                                <div className="bg-blue-600 h-1.5 rounded-full" style={{width: `${uploadProgress}%`}}></div>
                            </div>
                        )}
                        {imageUrl && <img src={imageUrl} alt="Preview" className="mt-2 rounded-md max-h-20" />}
                    </div>

                    <div className="border-t dark:border-gray-600 pt-4 space-y-4">
                        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400">Details (Optional)</h3>
                        <div>
                             <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Description</label>
                             <textarea value={description || ''} onChange={(e) => setDescription(e.target.value)} rows="2" placeholder="e.g. 'Abstract wall art, canvas print'" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Price</label>
                                <input type="number" value={price || ''} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 199.00" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                            </div>
                            <div>
                                <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Currency</label>
                                <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600">
                                    <option>USD</option>
                                    <option>EUR</option>
                                </select>
                            </div>
                        </div>
                        <div>
                             <label className="text-sm font-bold text-gray-600 dark:text-gray-400 block">Product Link</label>
                             <input type="url" value={linkUrl || ''} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://example.com/product" className="w-full mt-1 px-3 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" />
                        </div>
                    </div>

                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                    <button type="submit" disabled={isSubmitting} className={`w-full px-4 py-2 text-white font-medium rounded-lg ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
                        {isSubmitting ? 'Saving...' : 'Save Image'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export const DecorativeImageLibrary = ({ onSelectItem, ownerId, images, canManage, onEditItem, purpose }) => {
    const [imageToDelete, setImageToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteRequest = (e, item) => {
        e.stopPropagation();
        setImageToDelete(item);
    };

    const handleConfirmDelete = async () => {
        if (!imageToDelete) return;
        setIsDeleting(true);
        try {
            await deleteDecorativeImage(imageToDelete.id, imageToDelete.imageUrl);
        } catch (error) {
            console.error("Failed to delete image", error);
        } finally {
            setIsDeleting(false);
            setImageToDelete(null);
        }
    };
    
    const handleClick = (e, item) => {
        e.stopPropagation();
        if (purpose === 'add' || purpose === 'assign') {
            onSelectItem(item);
            return;
        }

        if (canManage && item.ownerId === ownerId) {
            onEditItem(item);
        } else {
            onSelectItem(item);
        }
    }

    return (
        <>
            <div className="flex justify-end mb-4 flex-shrink-0">
                {canManage && <button onClick={() => onEditItem(null)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    <Plus className="h-4 w-4" />
                    Add New
                </button>}
            </div>

            <div className="flex-grow overflow-y-auto pr-2 -mr-2">
                {images.length > 0 ? (
                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {images.map(item => (
                            <div key={item.id} onClick={(e) => handleClick(e, item)} className="group relative border dark:border-gray-700 rounded-lg cursor-pointer overflow-hidden aspect-square flex flex-col hover:shadow-lg hover:border-blue-500 dark:hover:border-blue-500 transition-all">
                                <div className="w-full h-full bg-cover bg-center bg-gray-100 dark:bg-gray-700" style={{backgroundImage: `url(${item.imageUrl})`}}>
                                </div>
                                <div className="p-2 bg-white/70 dark:bg-black/70 backdrop-blur-sm w-full">
                                    <p className="text-sm font-semibold truncate text-gray-800 dark:text-gray-200">{item.name}</p>
                                </div>
                                {canManage && item.ownerId === ownerId && (
                                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); onEditItem(item); }} 
                                            className="p-1.5 bg-white/50 dark:bg-black/50 backdrop-blur-sm rounded-full text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                                            aria-label={`Edit ${item.name}`}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button 
                                            onClick={(e) => handleDeleteRequest(e, item)} 
                                            className="p-1.5 bg-white/50 dark:bg-black/50 backdrop-blur-sm rounded-full text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400" 
                                            aria-label={`Delete ${item.name}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                        <X className="h-12 w-12" />
                        <p className="mt-4 text-lg">Your library is empty.</p>
                        {canManage && <p className="text-sm">Click "Add New" to create your first decorative image.</p>}
                    </div>
                )}
            </div>

            <DeleteConfirmationModal
                isOpen={!!imageToDelete}
                onClose={() => setImageToDelete(null)}
                onConfirm={handleConfirmDelete}
                item={imageToDelete}
                isDeleting={isDeleting}
            />
        </>
    );
};
